import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  HostListener,
} from "@angular/core";
import { ClippingStudioClip } from "app/js/src/domain/clipping-studio/clipping-studio-clip";
import {
  ReframeConfig,
  ReframeKeyframe,
  ReframeLayoutMode,
  ReframeBackgroundStyle,
  ReframeCropRect,
  getReframeConfigAtTime,
  normalizeSegmentReframes,
} from "app/js/src/domain/clipping-studio/clipping-studio-project";
import { drawReframeFrame } from "app/js/src/infrastructure/clipping-studio/clipping-studio-reframe-draw";
import { ClippingStudioAiReframeService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-ai-reframe.service";
import { AspectRatio } from "app/js/src/domain/clipping-studio/aspect-ratio";

export interface AutoReframeMediaContext {
  previewUrl: string;
  fps?: number;
  durationMs?: number;
  focusUrl?: string;
}

const DEFAULT_REFRAME: ReframeConfig = {
  layout: "fit",
  backgroundStyle: "black",
};

function parseAspectRatio(aspectRatio: AspectRatio): { w: number; h: number } {
  const parts = aspectRatio.trim().split(/\s*:\s*/);
  const w = parts.length >= 1 ? parseFloat(parts[0]) || 16 : 16;
  const h = parts.length >= 2 ? parseFloat(parts[1]) || 9 : 9;
  return { w, h };
}

@Component({
  selector: "evrideo-clipping-studio-auto-reframe-modal",
  templateUrl: "./clipping-studio-auto-reframe-modal.component.html",
  styleUrls: ["./clipping-studio-auto-reframe-modal.component.scss"],
})
export class ClippingStudioAutoReframeModalComponent implements OnInit, OnDestroy {
  @Input() clips: ClippingStudioClip[] = [];
  @Input() destinationId = "";
  @Input() aspectRatio: AspectRatio = AspectRatio.Aspect9x16;
  @Input() media: AutoReframeMediaContext = { previewUrl: "" };
  /** Main source duration (ms). With appendedMamSegments, used to resolve timeline time to source video. */
  @Input() mainDurationMs = 0;
  /** Appended MAM segments (previewUrl + durationMs). When non-empty, timeline time > mainDurationMs uses these sources. */
  @Input() appendedMamSegments: { previewUrl?: string; durationMs: number }[] = [];
  /** Clips with previewUrl (MAM-added in Edit mode). Sorted by order; used as additional video sources after appended. */
  @Input() editMamClips: { id: string; inMs: number; outMs: number; previewUrl: string }[] = [];
  /** Keyframes per clip (legacy ReframeConfig per clip is normalized to one keyframe at 0). */
  @Input() initialReframeKeyframes: Record<string, ReframeKeyframe[]> = {};

  /** Emits when user confirms and saves; parent should persist then close. */
  @Output() saveReframes = new EventEmitter<Record<string, ReframeKeyframe[]>>();
  /** Emits when modal is closed (saved: true if saved, false if exited without saving). */
  @Output() closed = new EventEmitter<boolean>();

  /** In-memory reframe keyframes per clip id (editable in modal). */
  reframes: Record<string, ReframeKeyframe[]> = {};
  /** Clip ids that have been manually edited in this session (show "Manual" badge; others show "AI"). */
  private manualClipIds = new Set<string>();
  /** 'ai' = AI Subject Tracking (read-only keyframes); 'manual' = Manual Adjustment (editable). */
  adjustmentMode: "ai" | "manual" = "ai";
  /** When true, the AI/Manual radio field is visible. Hidden when user selects Manual; shown again only on Reset to AI Suggestion. */
  showAdjustmentOptionsField = true;
  activeClipIndex = 0;
  /** Index of the keyframe being edited for the active clip (null = use config at current time). */
  activeKeyframeIndex: number | null = null;
  /** Current time within the active clip (ms). */
  currentTimeMs = 0;
  playing = false;
  private rafId: number | null = null;
  private _videoEl: HTMLVideoElement | null = null;
  /** Source index of the video currently playing (-1 = main, 0..n-1 = appended). Used in tick() to map currentTime back to timeline. */
  private currentPlaybackSourceIndex = -1;

  /** Dragging state for overlay crop rects. */
  private dragState: {
    which: "crop" | "cropResize" | "topCrop" | "bottomCrop";
    startNormX: number;
    startNormY: number;
    startRect: ReframeCropRect;
    /** Keyframe index being edited during this drag (from ensureKeyframeAtCurrentTime). */
    keyframeIndex: number;
  } | null = null;

  /** Epsilon (ms): treat keyframes at same time as currentTimeMs when within this range. */
  private static readonly KEYFRAME_TIME_EPSILON_MS = 50;

  /** Normalized radius for resize handle hit-test (bottom-right corner). */
  private static readonly RESIZE_HANDLE_NORM_RADIUS = 0.04;

  private boundMouseMove = (e: MouseEvent) => this.onOverlayMouseMove(e);
  private boundMouseUp = () => this.onOverlayMouseUp();

  readonly layoutModes: { value: ReframeLayoutMode; label: string }[] = [
    { value: "fill", label: "Fill" },
    { value: "fit", label: "Fit" },
    { value: "customBackground", label: "Custom + Background" },
    { value: "split50", label: "Split 50/50" },
  ];
  readonly backgroundStyles: { value: ReframeBackgroundStyle; label: string }[] = [
    { value: "black", label: "Black" },
    { value: "white", label: "White" },
    { value: "blur", label: "Blur" },
  ];

  @ViewChild("sourceVideo") sourceVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChildren("appendedVideo") appendedVideoRefs!: QueryList<ElementRef<HTMLVideoElement>>;
  @ViewChildren("editMamVideo") editMamVideoRefs!: QueryList<ElementRef<HTMLVideoElement>>;
  @ViewChild("sourceOverlay") sourceOverlayRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild("outputCanvas") outputCanvasRef!: ElementRef<HTMLCanvasElement>;

  /** Source overlay and output canvas logical size (for sharp drawing). */
  sourceOverlayWidth = 640;
  sourceOverlayHeight = 360;
  outputCanvasWidth = 360;
  outputCanvasHeight = 640;

  constructor(
    private cdr: ChangeDetectorRef,
    private aiReframeService: ClippingStudioAiReframeService,
  ) {}

  ngOnInit(): void {
    const normalized = normalizeSegmentReframes(
      this.initialReframeKeyframes as Record<string, ReframeConfig | ReframeKeyframe[]>,
    );
    this.reframes = {};
    this.clips.forEach((c) => {
      const kfs = normalized[c.id];
      this.reframes[c.id] =
        kfs?.length ? kfs.map((kf) => ({ timeMs: kf.timeMs, config: this.cloneConfig(kf.config) })) : [{ timeMs: 0, config: this.cloneConfig(DEFAULT_REFRAME) }];
    });
    if (this.clips.length) {
      this.activeClipIndex = 0;
      this.activeKeyframeIndex = null;
      this.currentTimeMs = 0;
    }
    const { w, h } = parseAspectRatio(this.aspectRatio);
    this.outputCanvasWidth = 360;
    this.outputCanvasHeight = Math.round(360 * h / w);
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.stopPlayback();
    document.removeEventListener("mousemove", this.boundMouseMove, true);
    document.removeEventListener("mouseup", this.boundMouseUp, true);
    this.dragState = null;
  }

  get activeClip(): ClippingStudioClip | null {
    if (!this.clips.length || this.activeClipIndex < 0 || this.activeClipIndex >= this.clips.length) return null;
    return this.clips[this.activeClipIndex];
  }

  get activeClipId(): string | null {
    const clip = this.activeClip;
    return clip ? clip.id : null;
  }

  /** True when the active clip is from MAM (not from main source); such clips have no AI keyframes. */
  get isActiveClipFromMam(): boolean {
    const clip = this.activeClip;
    return clip != null && clip.mamMediaId != null;
  }

  /** True if this clip has been manually edited in this session. */
  isClipManual(clipId: string): boolean {
    return this.manualClipIds.has(clipId);
  }

  /** Number of clips that are not manually edited (AI reframed). */
  get aiReframedCount(): number {
    return this.clips.length - this.manualClipIds.size;
  }

  private markActiveClipManual(): void {
    const id = this.activeClipId;
    if (id) this.manualClipIds.add(id);
    this.cdr.detectChanges();
  }

  private markAllClipsManual(): void {
    this.clips.forEach((c) => this.manualClipIds.add(c.id));
    this.cdr.detectChanges();
  }

  /** Sorted keyframes for the active clip. */
  get activeClipKeyframes(): ReframeKeyframe[] {
    const id = this.activeClipId;
    if (!id) return [];
    const kfs = this.reframes[id] ?? [];
    return [...kfs].sort((a, b) => a.timeMs - b.timeMs);
  }

  get currentReframe(): ReframeConfig {
    const id = this.activeClipId;
    if (!id) return { ...DEFAULT_REFRAME };
    const kfs = this.activeClipKeyframes;
    if (this.activeKeyframeIndex != null && this.activeKeyframeIndex >= 0 && this.activeKeyframeIndex < kfs.length) {
      return kfs[this.activeKeyframeIndex].config;
    }
    const resolved = getReframeConfigAtTime(kfs, this.currentTimeMs);
    return resolved ?? { ...DEFAULT_REFRAME };
  }

  /** Config to draw at the current playhead time (updates during playback; used for overlay and output). */
  get reframeConfigForDisplay(): ReframeConfig {
    const id = this.activeClipId;
    if (!id) return { ...DEFAULT_REFRAME };
    const kfs = this.activeClipKeyframes;
    const resolved = getReframeConfigAtTime(kfs, this.currentTimeMs);
    return resolved ?? { ...DEFAULT_REFRAME };
  }

  /** Index of the keyframe that is being edited (for sidebar and overlay). */
  private getEditableKeyframeIndex(): number {
    const id = this.activeClipId;
    if (!id) return 0;
    const kfs = this.activeClipKeyframes;
    if (!kfs.length) return 0;
    if (this.activeKeyframeIndex != null && this.activeKeyframeIndex >= 0 && this.activeKeyframeIndex < kfs.length) {
      return this.activeKeyframeIndex;
    }
    let idx = 0;
    for (let i = 0; i < kfs.length; i++) {
      if (kfs[i].timeMs <= this.currentTimeMs) idx = i;
    }
    return idx;
  }

  /**
   * Ensures a keyframe exists at the current video time (adds one if not present).
   * Returns the index of that keyframe; callers should update that keyframe's config.
   */
  private ensureKeyframeAtCurrentTime(): number {
    const id = this.activeClipId;
    if (!id) return -1;
    let kfs = [...(this.reframes[id] ?? [])].sort((a, b) => a.timeMs - b.timeMs);
    const epsilon = ClippingStudioAutoReframeModalComponent.KEYFRAME_TIME_EPSILON_MS;
    const at = this.currentTimeMs;
    const existingIdx = kfs.findIndex((kf) => Math.abs(kf.timeMs - at) <= epsilon);
    if (existingIdx >= 0) {
      this.activeKeyframeIndex = existingIdx;
      return existingIdx;
    }
    const newKf: ReframeKeyframe = { timeMs: at, config: this.cloneConfig(this.currentReframe) };
    kfs.push(newKf);
    kfs.sort((a, b) => a.timeMs - b.timeMs);
    this.reframes[id] = kfs;
    const newIdx = kfs.findIndex((kf) => kf === newKf);
    this.activeKeyframeIndex = newIdx >= 0 ? newIdx : kfs.length - 1;
    return this.activeKeyframeIndex;
  }

  get clipDurationMs(): number {
    const clip = this.activeClip;
    return clip ? Math.max(0, clip.outMs - clip.inMs) : 0;
  }

  /** Edit-mode MAM clips (with previewUrl), sorted by inMs for consistent ordering. */
  get orderedEditMamClips(): { id: string; inMs: number; outMs: number; previewUrl: string }[] {
    const list = this.editMamClips ?? [];
    return list.slice().sort((a, b) => a.inMs - b.inMs);
  }

  /**
   * Resolves a timeline position (ms) to the source video index and time within that source (seconds).
   * sourceIndex -1 = main, 0..n-1 = appended, n..n+m-1 = edit MAM clips.
   */
  private resolveTimelineToSource(timelineMs: number): { sourceIndex: number; timeSec: number } {
    const editMam = this.orderedEditMamClips;
    for (let i = 0; i < editMam.length; i++) {
      const clip = editMam[i];
      if (timelineMs >= clip.inMs && timelineMs < clip.outMs) {
        const timeInClipSec = (timelineMs - clip.inMs) / 1000;
        return {
          sourceIndex: (this.appendedMamSegments?.length ?? 0) + i,
          timeSec: Math.max(0, timeInClipSec),
        };
      }
    }
    const appended = this.appendedMamSegments ?? [];
    if (appended.length === 0 || timelineMs < this.mainDurationMs) {
      return { sourceIndex: -1, timeSec: Math.max(0, timelineMs / 1000) };
    }
    let cursorMs = this.mainDurationMs;
    for (let i = 0; i < appended.length; i++) {
      const endMs = cursorMs + appended[i].durationMs;
      if (timelineMs < endMs) {
        const timeInSegmentMs = timelineMs - cursorMs;
        return { sourceIndex: i, timeSec: Math.max(0, timeInSegmentMs / 1000) };
      }
      cursorMs = endMs;
    }
    const last = appended[appended.length - 1];
    return {
      sourceIndex: appended.length - 1,
      timeSec: Math.max(0, (last?.durationMs ?? 0) / 1000),
    };
  }

  /** Source index to show in the source column (-1 = main, 0..n-1 = appended). Used to show correct video for current time. */
  get activeSourceIndexForDisplay(): number {
    const clip = this.activeClip;
    if (!clip) return -1;
    const timelineMs = clip.inMs + this.currentTimeMs;
    return this.resolveTimelineToSource(timelineMs).sourceIndex;
  }

  /** Returns the video element for the given source index (-1 = main, 0..n-1 = appended, n.. = edit MAM). */
  private getVideoBySourceIndex(sourceIndex: number): HTMLVideoElement | null {
    if (sourceIndex === -1) {
      return this.sourceVideoRef?.nativeElement ?? null;
    }
    const appendedLen = this.appendedMamSegments?.length ?? 0;
    if (sourceIndex < appendedLen) {
      const refs = this.appendedVideoRefs?.toArray() ?? [];
      const el = refs[sourceIndex]?.nativeElement;
      return el ?? null;
    }
    const editRefs = this.editMamVideoRefs?.toArray() ?? [];
    const editIdx = sourceIndex - appendedLen;
    const el = editRefs[editIdx]?.nativeElement;
    return el ?? null;
  }

  /** Pause main and all appended and edit MAM videos. */
  private pauseAllVideos(): void {
    const main = this.sourceVideoRef?.nativeElement;
    if (main) main.pause();
    (this.appendedVideoRefs?.toArray() ?? []).forEach((r) => r?.nativeElement?.pause());
    (this.editMamVideoRefs?.toArray() ?? []).forEach((r) => r?.nativeElement?.pause());
  }

  private getActiveVideoForCurrentTime(): HTMLVideoElement | null {
    const clip = this.activeClip;
    if (!clip) return null;
    const timelineMs = clip.inMs + this.currentTimeMs;
    const { sourceIndex } = this.resolveTimelineToSource(timelineMs);
    return this.getVideoBySourceIndex(sourceIndex);
  }

  /** Current time within the active clip (timeline of the clip in the modal), formatted mm:ss. */
  get currentTimeFormatted(): string {
    const ms = this.currentTimeMs;
    const sec = Math.floor(ms / 1000);
    const min = Math.floor(sec / 60);
    return `${String(min).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
  }

  /** Total duration of all clips (sequence length), formatted mm:ss. */
  get totalTimeFormatted(): string {
    const totalMs = this.clips.reduce((acc, c) => acc + (c.outMs - c.inMs), 0);
    const sec = Math.floor(totalMs / 1000);
    const min = Math.floor(sec / 60);
    return `${String(min).padStart(2, "0")}:${String(sec % 60).padStart(2, "0")}`;
  }

  get showCropControls(): boolean {
    const layout = this.currentReframe.layout;
    return layout === "fill" || layout === "customBackground" || layout === "split50";
  }

  /** CSS aspect-ratio value (e.g. "9 / 16"). */
  get aspectRatioCss(): string {
    const { w, h } = parseAspectRatio(this.aspectRatio);
    return `${w} / ${h}`;
  }

  get isResizingFillCrop(): boolean {
    return this.dragState?.which === "cropResize";
  }

  get showBackgroundStyle(): boolean {
    const layout = this.currentReframe.layout;
    return layout === "fit" || layout === "customBackground";
  }

  setAdjustmentMode(mode: "ai" | "manual"): void {
    this.adjustmentMode = mode;
    if (mode === "manual") {
      this.showAdjustmentOptionsField = false;
    }
    this.cdr.detectChanges();
  }

  setLayout(mode: ReframeLayoutMode): void {
    const id = this.activeClipId;
    if (!id) return;
    this.markActiveClipManual();
    const idx = this.ensureKeyframeAtCurrentTime();
    if (idx < 0) return;
    const kfs = [...(this.reframes[id] ?? [])].sort((a, b) => a.timeMs - b.timeMs);
    if (idx >= kfs.length) return;
    const cfg = { ...kfs[idx].config, layout: mode };
    if (mode === "fill") cfg.crop = this.getFullFillCrop(this.getActiveVideoForCurrentTime());
    if (mode === "customBackground" && !cfg.crop) cfg.crop = this.defaultFillCrop();
    if (mode === "split50") {
      if (!cfg.topCrop) cfg.topCrop = this.defaultSplitTopCrop();
      if (!cfg.bottomCrop) cfg.bottomCrop = this.defaultSplitBottomCrop();
    }
    kfs[idx] = { ...kfs[idx], config: cfg };
    this.reframes[id] = kfs;
    this.cdr.detectChanges();
    this.drawCanvases();
  }

  setBackgroundStyle(style: ReframeBackgroundStyle): void {
    const id = this.activeClipId;
    if (!id) return;
    this.markActiveClipManual();
    const idx = this.ensureKeyframeAtCurrentTime();
    if (idx < 0) return;
    const kfs = [...(this.reframes[id] ?? [])].sort((a, b) => a.timeMs - b.timeMs);
    if (idx >= kfs.length) return;
    kfs[idx] = { ...kfs[idx], config: { ...kfs[idx].config, backgroundStyle: style } };
    this.reframes[id] = kfs;
    this.cdr.detectChanges();
    this.drawCanvases();
  }

  swapTopBottom(): void {
    const id = this.activeClipId;
    if (!id) return;
    this.markActiveClipManual();
    const idx = this.ensureKeyframeAtCurrentTime();
    if (idx < 0) return;
    const kfs = [...(this.reframes[id] ?? [])].sort((a, b) => a.timeMs - b.timeMs);
    if (idx >= kfs.length || kfs[idx].config.layout !== "split50") return;
    kfs[idx] = { ...kfs[idx], config: { ...kfs[idx].config, swapped: !kfs[idx].config.swapped } };
    this.reframes[id] = kfs;
    this.cdr.detectChanges();
    this.drawCanvases();
  }

  resetToAiSuggestion(): void {
    const id = this.activeClipId;
    const clip = this.activeClip;
    if (!id || !clip || this.isActiveClipFromMam) return;
    this.adjustmentMode = "ai";
    this.showAdjustmentOptionsField = true;
    this.manualClipIds.delete(id);
    const focusUrl = this.media?.focusUrl?.trim();
    if (focusUrl) {
      this.aiReframeService
        .fetchFocusScenes(focusUrl)
        .then((scenes) => {
          const keyframes = this.aiReframeService.computeKeyframesForClip(
            clip,
            scenes,
            this.aspectRatio,
          );
          this.reframes[id] = keyframes;
          this.activeKeyframeIndex = 0;
          this.cdr.detectChanges();
          this.drawCanvases();
        })
        .catch(() => {
          this.reframes[id] = [{ timeMs: 0, config: this.cloneConfig(DEFAULT_REFRAME) }];
          this.activeKeyframeIndex = 0;
          this.cdr.detectChanges();
          this.drawCanvases();
        });
    } else {
      this.reframes[id] = [{ timeMs: 0, config: this.cloneConfig(DEFAULT_REFRAME) }];
      this.activeKeyframeIndex = 0;
      this.cdr.detectChanges();
      this.drawCanvases();
    }
  }

  applyToAllClips(): void {
    this.markAllClipsManual();
    const current = this.currentReframe;
    this.clips.forEach((c) => {
      this.reframes[c.id] = [{ timeMs: 0, config: this.cloneConfig(current) }];
    });
    this.cdr.detectChanges();
  }

  deleteActiveKeyframe(): void {
    const id = this.activeClipId;
    if (!id || this.activeKeyframeIndex == null) return;
    this.markActiveClipManual();
    const kfs = [...(this.reframes[id] ?? [])].sort((a, b) => a.timeMs - b.timeMs);
    if (kfs.length <= 1) return;
    kfs.splice(this.activeKeyframeIndex, 1);
    this.reframes[id] = kfs;
    this.activeKeyframeIndex = null;
    this.cdr.detectChanges();
    this.drawCanvases();
  }

  selectKeyframe(index: number): void {
    this.activeKeyframeIndex = index;
    const kfs = this.activeClipKeyframes;
    if (index >= 0 && index < kfs.length) {
      this.currentTimeMs = kfs[index].timeMs;
      this.seekVideoToCurrent();
    }
    this.cdr.detectChanges();
    this.drawCanvases();
  }

  exitReframe(): void {
    this.closed.emit(false);
  }

  confirmAndSave(): void {
    const toEmit: Record<string, ReframeKeyframe[]> = {};
    for (const [id, kfs] of Object.entries(this.reframes)) {
      // Do not force-create `crop` for `fill` on save: when missing, runtime draw path
      // auto-computes a correct cover-crop based on the actual source video aspect.
      toEmit[id] = kfs.map((kf) => ({ ...kf, config: this.cloneConfig(kf.config) }));
    }
    this.saveReframes.emit(toEmit);
    this.closed.emit(true);
  }

  onClipSelect(index: number): void {
    this.stopPlayback();
    this.activeClipIndex = Math.max(0, Math.min(index, this.clips.length - 1));
    this.activeKeyframeIndex = null;
    this.currentTimeMs = 0;
    this.seekVideoToCurrent();
    this.cdr.detectChanges();
    this.drawCanvases();
  }

  onTimelineClick(event: MouseEvent): void {
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const pct = rect.width ? Math.max(0, Math.min(1, x / rect.width)) : 0;
    const wasPlaying = this.playing;
    this.currentTimeMs = pct * this.clipDurationMs;
    this.seekVideoToCurrent();
    this.cdr.detectChanges();
    this.drawCanvases();
    if (wasPlaying) {
      this.play();
    }
  }

  /** Convert overlay pixel coords to normalized 0–1. */
  private overlayToNorm(clientX: number, clientY: number): { nx: number; ny: number } | null {
    const overlay = this.sourceOverlayRef?.nativeElement;
    if (!overlay) return null;
    const rect = overlay.getBoundingClientRect();
    const w = overlay.width || this.sourceOverlayWidth;
    const h = overlay.height || this.sourceOverlayHeight;
    if (w <= 0 || h <= 0) return null;
    const px = ((clientX - rect.left) / rect.width) * w;
    const py = ((clientY - rect.top) / rect.height) * h;

    const video = this.getActiveVideoForCurrentTime();
    const vRect = this.getContainedVideoRect(w, h, video);
    if (!vRect) return null;

    const nx = (px - vRect.x) / vRect.w;
    const ny = (py - vRect.y) / vRect.h;
    if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return null;
    return { nx, ny };
  }

  /**
   * Computes the drawn video rectangle within the source overlay canvas when the
   * underlying <video> uses `object-fit: contain` inside a fixed-aspect wrapper.
   *
   * Returned rect is in overlay-canvas pixels (same space as `drawSourceOverlay`).
   */
  private getContainedVideoRect(
    outW: number,
    outH: number,
    video: HTMLVideoElement | null,
  ): { x: number; y: number; w: number; h: number } | null {
    if (outW <= 0 || outH <= 0) return null;
    const vidW = video?.videoWidth ?? 0;
    const vidH = video?.videoHeight ?? 0;
    if (vidW <= 0 || vidH <= 0) {
      // Fallback: treat as full-bleed until metadata is available.
      return { x: 0, y: 0, w: outW, h: outH };
    }
    const scale = Math.min(outW / vidW, outH / vidH);
    const w = vidW * scale;
    const h = vidH * scale;
    const x = (outW - w) / 2;
    const y = (outH - h) / 2;
    return { x, y, w, h };
  }

  private hitTestCrop(nx: number, ny: number): "crop" | "cropResize" | "topCrop" | "bottomCrop" | null {
    const cfg = this.reframeConfigForDisplay;
    const inRect = (r: ReframeCropRect) =>
      nx >= r.x && nx <= r.x + r.width && ny >= r.y && ny <= r.y + r.height;
    if (cfg.layout === "fill" && cfg.crop) {
      if (inRect(cfg.crop)) return "crop";
    }
    if (cfg.layout === "customBackground" && cfg.crop) {
      const r = cfg.crop;
      const brx = r.x + r.width;
      const bry = r.y + r.height;
      const distSq = (nx - brx) ** 2 + (ny - bry) ** 2;
      if (distSq <= ClippingStudioAutoReframeModalComponent.RESIZE_HANDLE_NORM_RADIUS ** 2)
        return "cropResize";
      if (inRect(r)) return "crop";
    }
    if (cfg.layout === "split50") {
      if (cfg.topCrop && inRect(cfg.topCrop)) return "topCrop";
      if (cfg.bottomCrop && inRect(cfg.bottomCrop)) return "bottomCrop";
    }
    return null;
  }

  onOverlayMouseDown(event: MouseEvent): void {
    if (this.adjustmentMode === "ai") return;
    const id = this.activeClipId;
    if (!id) return;
    const norm = this.overlayToNorm(event.clientX, event.clientY);
    if (!norm) return;
    const which = this.hitTestCrop(norm.nx, norm.ny);
    const idx = this.ensureKeyframeAtCurrentTime();
    if (idx < 0) return;
    const kfs = [...(this.reframes[id] ?? [])].sort((a, b) => a.timeMs - b.timeMs);
    if (idx >= kfs.length) return;
    const cfg = kfs[idx].config;

    if (!which) {
      if (cfg.layout === "fill" && cfg.crop) {
        this.markActiveClipManual();
        const r = cfg.crop;
        const x = Math.max(0, Math.min(1 - r.width, norm.nx - r.width / 2));
        kfs[idx] = { ...kfs[idx], config: { ...cfg, crop: { ...r, x } } };
        this.reframes[id] = kfs;
        this.cdr.detectChanges();
        this.drawCanvases();
      } else if (cfg.layout === "customBackground" && cfg.crop) {
        this.markActiveClipManual();
        const r = cfg.crop;
        let x = norm.nx - r.width / 2;
        let y = norm.ny - r.height / 2;
        x = Math.max(0, Math.min(1 - r.width, x));
        y = Math.max(0, Math.min(1 - r.height, y));
        kfs[idx] = { ...kfs[idx], config: { ...cfg, crop: { ...r, x, y } } };
        this.reframes[id] = kfs;
        this.cdr.detectChanges();
        this.drawCanvases();
      }
      return;
    }

    const rect =
      which === "crop" || which === "cropResize"
        ? cfg.crop
        : which === "topCrop"
          ? cfg.topCrop
          : cfg.bottomCrop;
    if (!rect) return;
    event.preventDefault();
    this.markActiveClipManual();
    this.dragState = {
      which,
      startNormX: norm.nx,
      startNormY: norm.ny,
      startRect: { ...rect },
      keyframeIndex: idx,
    };
    document.addEventListener("mousemove", this.boundMouseMove, true);
    document.addEventListener("mouseup", this.boundMouseUp, true);
  }

  onOverlayMouseMove(event: MouseEvent): void {
    if (!this.dragState) return;
    event.preventDefault();
    const norm = this.overlayToNorm(event.clientX, event.clientY);
    const id = this.activeClipId;
    if (!id) return;
    const kfs = [...(this.reframes[id] ?? [])].sort((a, b) => a.timeMs - b.timeMs);
    const idx = this.dragState.keyframeIndex;
    if (idx >= kfs.length) return;
    const cfg = kfs[idx].config;
    const deltaX = norm ? norm.nx - this.dragState.startNormX : 0;
    const deltaY = norm ? norm.ny - this.dragState.startNormY : 0;
    const { which, startRect } = this.dragState;

    if (which === "cropResize" && cfg.crop && cfg.layout === "customBackground") {
      const r = cfg.crop;
      const x = r.x;
      const y = r.y;
      let nx = norm ? norm.nx : r.x + r.width;
      let ny = norm ? norm.ny : r.y + r.height;
      nx = Math.max(x + 0.05, Math.min(1, nx));
      ny = Math.max(y + 0.05, Math.min(1, ny));
      let w = nx - x;
      let h = ny - y;
      w = Math.max(0.05, Math.min(1 - x, w));
      h = Math.max(0.05, Math.min(1 - y, h));
      kfs[idx] = { ...kfs[idx], config: { ...cfg, crop: { x, y, width: w, height: h } } };
    } else if (which === "crop" && cfg.layout === "fill" && cfg.crop) {
      const r = cfg.crop;
      let x = startRect.x + deltaX;
      x = Math.max(0, Math.min(1 - r.width, x));
      kfs[idx] = { ...kfs[idx], config: { ...cfg, crop: { ...r, x } } };
    } else if (which === "crop" && cfg.crop) {
      const r = cfg.crop;
      let x = startRect.x + deltaX;
      let y = startRect.y + deltaY;
      x = Math.max(0, Math.min(1 - r.width, x));
      y = Math.max(0, Math.min(1 - r.height, y));
      kfs[idx] = { ...kfs[idx], config: { ...cfg, crop: { ...r, x, y } } };
    } else if (which === "topCrop" && cfg.topCrop) {
      const r = cfg.topCrop;
      let x = startRect.x + deltaX;
      x = Math.max(0, Math.min(1 - r.width, x));
      kfs[idx] = { ...kfs[idx], config: { ...cfg, topCrop: { ...r, x } } };
    } else if (which === "bottomCrop" && cfg.bottomCrop) {
      const r = cfg.bottomCrop;
      let x = startRect.x + deltaX;
      x = Math.max(0, Math.min(1 - r.width, x));
      kfs[idx] = { ...kfs[idx], config: { ...cfg, bottomCrop: { ...r, x } } };
    }
    this.reframes[id] = kfs;
    this.cdr.detectChanges();
    this.drawCanvases();
  }

  onOverlayMouseUp(): void {
    if (this.dragState) {
      document.removeEventListener("mousemove", this.boundMouseMove, true);
      document.removeEventListener("mouseup", this.boundMouseUp, true);
    }
    this.dragState = null;
  }

  play(): void {
    const clip = this.activeClip;
    if (!clip || !this.media.previewUrl) return;
    const timelineMs = clip.inMs + this.currentTimeMs;
    const { sourceIndex, timeSec } = this.resolveTimelineToSource(timelineMs);
    const video = this.getVideoBySourceIndex(sourceIndex);
    if (!video) return;
    this.playing = true;
    this.currentPlaybackSourceIndex = sourceIndex;
    this._videoEl = video;
    this.pauseAllVideos();
    video.currentTime = timeSec;
    video.play().catch(() => (this.playing = false));
    this.tick();
  }

  pause(): void {
    this.stopPlayback();
  }

  togglePlay(): void {
    if (this.playing) this.pause();
    else this.play();
  }

  /** One frame duration in ms (from media.fps). */
  private get frameDurationMs(): number {
    const fps = this.media?.fps ?? 30;
    return 1000 / fps;
  }

  frameBack(steps = 1): void {
    const stepMs = this.frameDurationMs * steps;
    this.currentTimeMs = Math.max(0, this.currentTimeMs - stepMs);
    this.seekVideoToCurrent();
    this.cdr.detectChanges();
    this.drawCanvases();
  }

  frameForward(steps = 1): void {
    const stepMs = this.frameDurationMs * steps;
    this.currentTimeMs = Math.min(this.clipDurationMs, this.currentTimeMs + stepMs);
    this.seekVideoToCurrent();
    this.cdr.detectChanges();
    this.drawCanvases();
  }

  @HostListener("document:keydown", ["$event"])
  onDocumentKeyDown(event: KeyboardEvent): void {
    if (
      event.target &&
      (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)
    ) {
      return;
    }
    if (event.code === "Space" || event.code === "KeyK") {
      this.togglePlay();
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.code === "ArrowLeft") {
      this.frameBack(event.shiftKey ? 5 : 1);
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.code === "ArrowRight") {
      this.frameForward(event.shiftKey ? 5 : 1);
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if ((event.code === "Delete" || event.code === "Backspace") && this.activeKeyframeIndex != null && this.activeClipKeyframes.length > 1) {
      this.deleteActiveKeyframe();
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onVideoTimeUpdate(): void {
    const clip = this.activeClip;
    const video = this.sourceVideoRef?.nativeElement;
    if (!clip || !video) return;
    const t = video.currentTime * 1000;
    if (t >= clip.inMs && t <= clip.outMs) {
      this.currentTimeMs = t - clip.inMs;
      this.cdr.detectChanges();
    }
  }

  onVideoSeeked(): void {
    this.drawCanvases();
  }

  onSourceVideoRef(video: HTMLVideoElement | null): void {
    this._videoEl = video;
    if (video && this.activeClip) {
      this.seekVideoToCurrent();
    }
  }

  private get outputAspectRatio(): number {
    const { w, h } = parseAspectRatio(this.aspectRatio);
    return w / h;
  }

  /** Full 9:16 (destination aspect) crop for Fill: maximal such rect in 16:9 source, centered, not resizable. */
  private getFullFillCrop(video: HTMLVideoElement | null): ReframeCropRect {
    const { w: aw, h: ah } = parseAspectRatio(this.aspectRatio);
    const targetAspect = aw / ah;
    const vidW = video?.videoWidth ?? 0;
    const vidH = video?.videoHeight ?? 0;
    const sourceAspect = vidW > 0 && vidH > 0 ? vidW / vidH : targetAspect;

    if (sourceAspect > targetAspect) {
      // Source is wider than target -> crop width, keep full height.
      const width = targetAspect / sourceAspect;
      return { x: (1 - width) / 2, y: 0, width, height: 1 };
    }
    // Source is taller/narrower than target -> crop height, keep full width.
    const height = sourceAspect / targetAspect;
    return { x: 0, y: (1 - height) / 2, width: 1, height };
  }

  /** Default crop for Custom + Background: smaller than full so it can be moved and resized. ~55% of max at destination aspect, centered. */
  private defaultFillCrop(): ReframeCropRect {
    const targetAspect = parseAspectRatio(this.aspectRatio).w / parseAspectRatio(this.aspectRatio).h;
    const scale = 0.55;
    const h = scale;
    const w = h * targetAspect;
    const x = (1 - w) / 2;
    const y = (1 - h) / 2;
    return { x, y, width: w, height: h };
  }

  /** Split: left vertical strip (full height), drawn on top half of output. */
  private defaultSplitTopCrop(): ReframeCropRect {
    return { x: 0, y: 0, width: 0.5, height: 1 };
  }

  /** Split: right vertical strip (full height), drawn on bottom half of output. */
  private defaultSplitBottomCrop(): ReframeCropRect {
    return { x: 0.5, y: 0, width: 0.5, height: 1 };
  }

  private cloneConfig(c: ReframeConfig): ReframeConfig {
    return {
      ...c,
      crop: c.crop ? { ...c.crop } : undefined,
      topCrop: c.topCrop ? { ...c.topCrop } : undefined,
      bottomCrop: c.bottomCrop ? { ...c.bottomCrop } : undefined,
    };
  }

  /** Maps (sourceIndex, timeSec) to timeline position in ms. */
  private timelineMsFromVideoTime(sourceIndex: number, timeSec: number): number {
    if (sourceIndex === -1) return timeSec * 1000;
    const appendedLen = this.appendedMamSegments?.length ?? 0;
    if (sourceIndex < appendedLen) {
      const appended = this.appendedMamSegments ?? [];
      let cursorMs = this.mainDurationMs;
      for (let i = 0; i < sourceIndex && i < appended.length; i++) {
        cursorMs += appended[i].durationMs;
      }
      return cursorMs + timeSec * 1000;
    }
    const editMam = this.orderedEditMamClips;
    const editIdx = sourceIndex - appendedLen;
    const clip = editMam[editIdx];
    return clip ? clip.inMs + timeSec * 1000 : 0;
  }

  seekVideoToCurrent(): void {
    const clip = this.activeClip;
    if (!clip) return;
    const timelineMs = clip.inMs + this.currentTimeMs;
    const { sourceIndex, timeSec } = this.resolveTimelineToSource(timelineMs);
    const video = this.getVideoBySourceIndex(sourceIndex);
    if (!video) return;
    this.pauseAllVideos();
    video.currentTime = timeSec;
  }

  private stopPlayback(): void {
    this.playing = false;
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.pauseAllVideos();
    this.cdr.detectChanges();
  }

  private tick = (): void => {
    const clip = this.activeClip;
    const video = this._videoEl;
    if (!clip || !video || !this.playing) return;
    const timeSec = video.currentTime;
    const timelineMs = this.timelineMsFromVideoTime(this.currentPlaybackSourceIndex, timeSec);
    this.currentTimeMs = Math.max(0, Math.min(this.clipDurationMs, timelineMs - clip.inMs));
    if (this.currentTimeMs >= this.clipDurationMs) {
      this.pause();
      return;
    }
    const appendedLen = this.appendedMamSegments?.length ?? 0;
    const editMam = this.orderedEditMamClips;
    let segmentEndMs: number;
    if (this.currentPlaybackSourceIndex === -1) {
      segmentEndMs = this.mainDurationMs;
    } else if (this.currentPlaybackSourceIndex < appendedLen) {
      const appended = this.appendedMamSegments ?? [];
      segmentEndMs = this.mainDurationMs + appended
        .slice(0, this.currentPlaybackSourceIndex + 1)
        .reduce((s, seg) => s + seg.durationMs, 0);
    } else {
      const editIdx = this.currentPlaybackSourceIndex - appendedLen;
      const mamClip = editMam[editIdx];
      segmentEndMs = mamClip ? mamClip.outMs : timelineMs + 1;
    }
    if (timelineMs >= segmentEndMs) {
      const nextIndex = this.currentPlaybackSourceIndex + 1;
      const totalSources = appendedLen + editMam.length;
      if (nextIndex < totalSources) {
        const nextVideo = this.getVideoBySourceIndex(nextIndex);
        if (nextVideo) {
          this.pauseAllVideos();
          nextVideo.currentTime = 0;
          nextVideo.play().catch(() => (this.playing = false));
          this._videoEl = nextVideo;
          this.currentPlaybackSourceIndex = nextIndex;
        } else {
          this.pause();
          return;
        }
      } else {
        this.pause();
        return;
      }
    }
    this.drawCanvases();
    this.cdr.detectChanges();
    this.rafId = requestAnimationFrame(this.tick);
  };

  /** Draw source overlay (crop rects) and output canvas. Called from template and tick. */
  drawCanvases(): void {
    this.drawSourceOverlay();
    this.drawOutputCanvas();
  }

  private drawSourceOverlay(): void {
    const overlay = this.sourceOverlayRef?.nativeElement;
    if (!overlay) return;
    const w = overlay.width || this.sourceOverlayWidth;
    const h = overlay.height || this.sourceOverlayHeight;
    if (w <= 0 || h <= 0) return;
    const ctx = overlay.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, w, h);
    const vRect = this.getContainedVideoRect(w, h, this.getActiveVideoForCurrentTime());
    const cfg = this.reframeConfigForDisplay;
    if (cfg.layout === "fit") return;
    const drawRect = (r: ReframeCropRect, label?: string) => {
      if (!vRect) return;
      const x = vRect.x + r.x * vRect.w;
      const y = vRect.y + r.y * vRect.h;
      const rw = r.width * vRect.w;
      const rh = r.height * vRect.h;
      ctx.fillStyle = "rgba(45, 187, 252, 0.12)";
      ctx.fillRect(x, y, rw, rh);
      ctx.strokeStyle = "rgba(45, 187, 252, 0.95)";
      ctx.lineWidth = 3;
      ctx.strokeRect(x, y, rw, rh);
      if (label) {
        ctx.font = "14px Open Sans, sans-serif";
        const paddingH = 6;
        const paddingV = 3;
        const metrics = ctx.measureText(label);
        const tagW = metrics.width + paddingH * 2;
        const tagH = 16 + paddingV * 2;
        const tagX = x + 6;
        const tagY = y + 4;
        const radius = 4;
        ctx.fillStyle = "rgba(45, 187, 252, 0.7)";
        ctx.beginPath();
        if (typeof ctx.roundRect === "function") {
          ctx.roundRect(tagX, tagY, tagW, tagH, radius);
        } else {
          ctx.moveTo(tagX + radius, tagY);
          ctx.lineTo(tagX + tagW - radius, tagY);
          ctx.arc(tagX + tagW - radius, tagY + radius, radius, -Math.PI / 2, 0);
          ctx.lineTo(tagX + tagW, tagY + tagH - radius);
          ctx.arc(tagX + tagW - radius, tagY + tagH - radius, radius, 0, Math.PI / 2);
          ctx.lineTo(tagX + radius, tagY + tagH);
          ctx.arc(tagX + radius, tagY + tagH - radius, radius, Math.PI / 2, Math.PI);
          ctx.lineTo(tagX, tagY + radius);
          ctx.arc(tagX + radius, tagY + radius, radius, Math.PI, (3 * Math.PI) / 2);
          ctx.closePath();
        }
        ctx.fill();
        ctx.fillStyle = "#fff";
        ctx.textBaseline = "middle";
        ctx.fillText(label, tagX + paddingH, tagY + tagH / 2);
      }
    };
    if (cfg.layout === "fill") {
      const crop = cfg.crop ?? this.getFullFillCrop(this.getActiveVideoForCurrentTime());
      drawRect(crop);
    }
    if (cfg.layout === "customBackground") {
      const crop = cfg.crop;
      if (crop) {
        drawRect(crop);
        if (this.adjustmentMode === "manual") {
          if (!vRect) return;
          const r = crop;
          const cx = vRect.x + (r.x + r.width) * vRect.w;
          const cy = vRect.y + (r.y + r.height) * vRect.h;
          const radius = Math.min(14, 0.025 * Math.min(w, h));
          ctx.fillStyle = "rgba(45, 187, 252, 0.9)";
          ctx.strokeStyle = "rgba(255, 255, 255, 0.95)";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(cx, cy, radius, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
      }
    }
    if (cfg.layout === "split50") {
      if (cfg.topCrop) drawRect(cfg.topCrop, cfg.swapped ? "BOTTOM CROP" : "TOP CROP");
      if (cfg.bottomCrop) drawRect(cfg.bottomCrop, cfg.swapped ? "TOP CROP" : "BOTTOM CROP");
    }
  }

  private drawOutputCanvas(): void {
    const outCanvas = this.outputCanvasRef?.nativeElement;
    const clip = this.activeClip;
    if (!outCanvas || !clip) return;
    const timelineMs = clip.inMs + this.currentTimeMs;
    const { sourceIndex, timeSec } = this.resolveTimelineToSource(timelineMs);
    const video = this.getVideoBySourceIndex(sourceIndex);
    if (!video || video.readyState < 2) return;
    if (Math.abs(video.currentTime - timeSec) > 0.05) {
      video.currentTime = timeSec;
    }
    const ctx = outCanvas.getContext("2d");
    if (!ctx) return;
    const cfg = this.reframeConfigForDisplay;
    const configToDraw =
      cfg.layout === "fill"
        ? { ...cfg, crop: cfg.crop ?? this.getFullFillCrop(video) }
        : cfg;
    drawReframeFrame(
      video,
      ctx,
      outCanvas.width,
      outCanvas.height,
      configToDraw,
    );
  }

  ngAfterViewInit(): void {
    const video = this.sourceVideoRef?.nativeElement;
    if (video) {
      this.onSourceVideoRef(video);
      video.addEventListener("timeupdate", () => this.onVideoTimeUpdate());
      video.addEventListener("seeked", () => this.onVideoSeeked());
      video.addEventListener("loadeddata", () => this.drawCanvases());
      video.addEventListener("canplay", () => this.drawCanvases());
      if (video.readyState >= 2) {
        this.seekVideoToCurrent();
        this.drawCanvases();
      }
    }
    setTimeout(() => this.drawCanvases(), 100);
    setTimeout(() => this.drawCanvases(), 500);
    if (this.appendedMamSegments?.length || this.orderedEditMamClips.length) {
      setTimeout(() => {
        this.seekVideoToCurrent();
        this.drawCanvases();
      }, 150);
    }
  }
}
